import React from 'react';

import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, Typography, IconButton } from '@material-ui/core';
import { Add, Delete, ArrowUpward, ArrowDownward } from '@material-ui/icons';
import { FieldArray } from 'formik';
import type { FormikProps } from 'formik';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormRichTextInput from 'components/FormRichTextInput';
import FormTextInput from 'components/FormTextInput/FormTextInput';

import type { ActionTemplateFormValues, ChecklistStep } from '../../ActionTemplateForm.types';

import useStyles from './ActionTemplateSteps.styles';

type ActionTemplateStepsProps = {
  formik: FormikProps<ActionTemplateFormValues>;
  loading: boolean;
};

const ActionTemplateSteps: React.FC<ActionTemplateStepsProps> = ({ formik, loading }) => {
  const { values, errors, touched, setFieldValue } = formik;
  const styles = useStyles();

  // Check if checklist has validation error
  const checklistError = errors.checklist;
  const checklistTouched = touched.checklist;
  const showChecklistError = checklistTouched && Boolean(checklistError) && values.checklist.length === 0;

  // Helper to convert boolean to semantic string for select component
  const getMandatoryValue = (value: boolean | string | undefined): string => {
    if (typeof value === 'boolean') {
      return value ? 'mandatory' : 'optional';
    }
    if (typeof value === 'string') {
      return value;
    }
    return 'mandatory'; // default
  };

  // Helper to handle select change and convert semantic string back to boolean
  const handleMandatoryChange = (index: number) => (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const stringValue = event.target.value as string;
    const booleanValue = stringValue === 'mandatory';
    setFieldValue(`checklist.${index}.isMandatory`, booleanValue);
  };

  return (
    <FieldArray name='checklist'>
      {({ push, remove, move }) => (
        <div>
          {values.checklist.map((step: ChecklistStep, index: number) => (
            <Paper key={step.id} style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant='h6'>Step {index + 1}</Typography>
              <CenteredGrid>
                <FormTextInput className={styles.marginBottom} formik={formik} id={`checklist.${index}.title`} label='Title' required />
                <FormRichTextInput
                  className={styles.richTextMarginBottom}
                  formik={formik}
                  id={`checklist.${index}.instruction`}
                  label='Instruction'
                  required
                />
                <FormControl
                  className={styles.marginBottom}
                  error={get(touched, `checklist.${index}.isMandatory`) && Boolean(get(errors, `checklist.${index}.isMandatory`))}
                  variant='outlined'
                >
                  <InputLabel id={`checklist.${index}.isMandatory-label`}>Mandatory *</InputLabel>
                  <Select
                    id={`checklist.${index}.isMandatory`}
                    label='Mandatory *'
                    labelId={`checklist.${index}.isMandatory-label`}
                    onBlur={() => formik.setFieldTouched(`checklist.${index}.isMandatory`, true)}
                    onChange={handleMandatoryChange(index)}
                    value={getMandatoryValue(get(values, `checklist.${index}.isMandatory`))}
                  >
                    <MenuItem value='mandatory'>Yes</MenuItem>
                    <MenuItem value='optional'>No</MenuItem>
                  </Select>
                  {get(touched, `checklist.${index}.isMandatory`) && get(errors, `checklist.${index}.isMandatory`) && (
                    <FormHelperText error>{get(errors, `checklist.${index}.isMandatory`)}</FormHelperText>
                  )}
                </FormControl>
                <FormTextInput
                  className={styles.marginBottom}
                  formik={formik}
                  id={`checklist.${index}.relatedReading`}
                  label='Related reading'
                />
                <FormTextInput formik={formik} id={`checklist.${index}.relevantFor`} label='Relevant for' />
              </CenteredGrid>
              <IconButton disabled={index === 0} onClick={() => move(index, index - 1)}>
                <ArrowUpward />
              </IconButton>
              <IconButton disabled={index === values.checklist.length - 1} onClick={() => move(index, index + 1)}>
                <ArrowDownward />
              </IconButton>
              <IconButton onClick={() => remove(index)}>
                <Delete />
              </IconButton>
            </Paper>
          ))}
          {showChecklistError && (
            <FormHelperText error style={{ marginBottom: 16 }}>
              {typeof checklistError === 'string' ? checklistError : 'At least one step is required'}
            </FormHelperText>
          )}
          <div className={styles.actionButtons}>
            <Button
              className={showChecklistError ? styles.addStepButtonError : styles.addStepButton}
              onClick={() => {
                push({ id: uuidv4(), title: '', instruction: '', isMandatory: true, relatedReading: '', relevantFor: '' });
              }}
              startIcon={<Add />}
              variant='outlined'
            >
              Add Step
            </Button>
            <ColoredButton
              className={styles.saveButton}
              customColor='secondary'
              disabled={loading}
              icon={false}
              showLoader={loading}
              textLike={false}
              type='submit'
              variant='outlined'
            >
              Save Template
            </ColoredButton>
          </div>
        </div>
      )}
    </FieldArray>
  );
};

export default ActionTemplateSteps;
